/**
 * components/ImageEditor.jsx
 * ---------------------------
 * Crop + rotate + adjust modal for card photos, used in BatchCapturePage and ScanPage.
 *
 * Props:
 *   file      — File object (from camera capture or file input)
 *   onSave    — callback(editedFile: File) — called with cropped JPEG
 *   onCancel  — callback() — user dismissed without saving
 *   title     — optional modal heading (e.g. "Edit Front Photo")
 *
 * Uses react-cropper (cropperjs v1 wrapper) for:
 *   - EXIF rotation handling (preserves phone portrait orientation)
 *   - Pinch-zoom / drag on mobile
 *   - Aspect ratio lock to standard card (2.5 × 3.5) or landscape (3.5 × 2.5)
 *   - Rotate CW / CCW buttons
 *
 * Additional controls:
 *   - Crop toggle (off by default — click to show/hide crop box)
 *   - Portrait / Landscape toggle (updates aspect ratio live)
 *   - Brightness, Contrast, Saturation sliders (applied to export canvas)
 *   - Output always exported at 1500×2100 (portrait) or 2100×1500 (landscape)
 */
import React, { useRef, useState, useEffect } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import "./ImageEditor.css";
import PropTypes from "prop-types";

export default function ImageEditor({ file, onSave, onCancel, title = "Edit Photo", onDelete }) {
  const cropperRef = useRef(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orientation, setOrientation] = useState("portrait");
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [cropEnabled, setCropEnabled] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Create an object URL for the file and revoke on cleanup
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Load saved adjustment defaults on mount
  useEffect(() => {
    const saved = localStorage.getItem("cardstoard_adjust_defaults");
    if (!saved) return;
    try {
      const { brightness: b, contrast: c, saturation: s } = JSON.parse(saved);
      if (b) setBrightness(b);
      if (c) setContrast(c);
      if (s) setSaturation(s);
    } catch {}
  }, []);

  const rotate = (deg) => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) cropper.rotate(deg);
  };

  const toggleCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    if (cropEnabled) {
      cropper.clear();
      setCropEnabled(false);
    } else {
      cropper.crop();
      setCropEnabled(true);
      setTimeout(() => loadCropDefault(orientation), 50);
    }
  };

  const loadCropDefault = (orient) => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    const saved = localStorage.getItem(`cardstoard_crop_default_${orient}`);
    if (!saved) return;
    try {
      const { x, y, w, h } = JSON.parse(saved);
      const canvas = cropper.getCanvasData();
      cropper.setCropBoxData({
        left:   canvas.left + x * canvas.width,
        top:    canvas.top  + y * canvas.height,
        width:  w * canvas.width,
        height: h * canvas.height,
      });
    } catch {}
  };

  const saveDefaults = () => {
    localStorage.setItem("cardstoard_adjust_defaults",
      JSON.stringify({ brightness, contrast, saturation }));
    if (cropEnabled) {
      const cropper = cropperRef.current?.cropper;
      if (cropper) {
        const canvas = cropper.getCanvasData();
        const box = cropper.getCropBoxData();
        localStorage.setItem(`cardstoard_crop_default_${orientation}`, JSON.stringify({
          x: (box.left - canvas.left) / canvas.width,
          y: (box.top  - canvas.top)  / canvas.height,
          w: box.width  / canvas.width,
          h: box.height / canvas.height,
        }));
      }
    }
  };

  const toggleOrientation = () => {
    setOrientation(prev => {
      const next = prev === "portrait" ? "landscape" : "portrait";
      cropperRef.current?.cropper?.setAspectRatio(next === "portrait" ? 2.5 / 3.5 : 3.5 / 2.5);
      if (cropEnabled) setTimeout(() => loadCropDefault(next), 50);
      return next;
    });
  };

  const handleReset = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) { cropper.reset(); cropper.clear(); }
    setCropEnabled(false);
    setBrightness(100); setContrast(100); setSaturation(100);
    localStorage.removeItem("cardstoard_adjust_defaults");
    localStorage.removeItem("cardstoard_crop_default_portrait");
    localStorage.removeItem("cardstoard_crop_default_landscape");
  };

  const handleSave = () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;
    setSaving(true);

    const targetW = orientation === "portrait" ? 1500 : 2100;
    const targetH = orientation === "portrait" ? 2100 : 1500;

    // Get natural-size crop first, then draw into a fixed-dimension canvas.
    // getCroppedCanvas width/height options don't reliably upscale in all cropperjs builds.
    // When no crop box is active, getCroppedCanvas returns the full rotated image canvas.
    const naturalCanvas = cropper.getCroppedCanvas();
    if (!naturalCanvas) { setSaving(false); return; }

    const filterStr = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    const needsFilter = brightness !== 100 || contrast !== 100 || saturation !== 100;

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = targetW;
    exportCanvas.height = targetH;
    const ctx = exportCanvas.getContext("2d");
    if (needsFilter) ctx.filter = filterStr;
    ctx.drawImage(naturalCanvas, 0, 0, targetW, targetH);

    exportCanvas.toBlob(  // always 1500×2100 (portrait) or 2100×1500 (landscape)
      (blob) => {
        if (!blob) { setSaving(false); return; }
        const editedFile = new File([blob], file.name || "card-photo.jpg", { type: "image/jpeg" });
        onSave(editedFile);
        setSaving(false);
      },
      "image/jpeg",
      0.92
    );
  };

  if (!objectUrl) return null;

  const filterStyle = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;

  return (
    <div className="image-editor-overlay" onClick={onCancel}>
      <div className="image-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="image-editor-header">
          <span style={{ fontWeight: 600, fontSize: "1.05rem", color: "var(--accent-blue-dark)" }}>{title}</span>
        </div>

        <div className="image-editor-crop-area">
          <div style={{ filter: filterStyle, height: "100%", width: "100%" }}>
            <Cropper
              ref={cropperRef}
              src={objectUrl}
              style={{ height: "100%", width: "100%" }}
              aspectRatio={2.5 / 3.5}
              viewMode={1}
              autoCrop={false}
              rotatable={true}
              scalable={false}
              zoomable={true}
              guides={true}
              background={false}
              responsive={true}
              ready={() => {
                cropperRef.current?.cropper?.clear();
              }}
            />
          </div>
        </div>

        <div className="image-editor-adjustments">
          <label>
            Bright  {/* NOSONAR */}
            <input type="range" min="50" max="150" value={brightness} onChange={e => setBrightness(+e.target.value)} />
          </label>
          <label>
            Contrast  {/* NOSONAR */}
            <input type="range" min="50" max="150" value={contrast} onChange={e => setContrast(+e.target.value)} />
          </label>
          <label>
            Sat  {/* NOSONAR */}
            <input type="range" min="0" max="200" value={saturation} onChange={e => setSaturation(+e.target.value)} />
          </label>
        </div>

        <div className="image-editor-controls">
          <div className="image-editor-controls-row">
            <button className="nav-btn secondary" onClick={() => rotate(-90)} title="Rotate counter-clockwise">↺ CCW</button>
            <button className="nav-btn secondary" onClick={() => rotate(90)} title="Rotate clockwise">CW ↻</button>
            <button
              className="nav-btn secondary"
              onClick={toggleCrop}
              title={cropEnabled ? "Hide crop box" : "Show crop box"}
              style={cropEnabled ? { background: "var(--accent-blue-dark)", color: "#fff", borderColor: "var(--accent-blue-dark)" } : {}}
            >✂ Crop</button>
            <button className="nav-btn secondary" onClick={toggleOrientation} title="Toggle portrait/landscape">
              {orientation === "portrait" ? "↔ Landscape" : "↕ Portrait"}
            </button>
          </div>
          <div className="image-editor-controls-row">
            <button className="nav-btn secondary" onClick={handleReset} title="Reset to defaults and clear saved settings">Reset</button>
            <button className="nav-btn secondary" onClick={saveDefaults} title="Save current adjustments and crop position as defaults">Save Default</button>
            <button className="nav-btn secondary" onClick={onCancel}>Cancel</button>
          </div>
          <div className="image-editor-controls-row">
            <button className="nav-btn" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Use Photo"}</button>
          </div>
          {onDelete && (
            <div className="image-editor-controls-row">
              {deleteConfirm ? (
                <>
                  <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>Delete this photo?</span>
                  <button className="nav-btn" style={{ background: "#c62828", borderColor: "#c62828" }} onClick={onDelete}>Yes, Delete</button>
                  <button className="nav-btn secondary" onClick={() => setDeleteConfirm(false)}>No</button>
                </>
              ) : (
                <button className="nav-btn secondary" style={{ color: "#c62828", borderColor: "#c62828" }}
                  onClick={() => setDeleteConfirm(true)}>Delete Photo</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ImageEditor.propTypes = {
  file: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  title: PropTypes.string,
  onDelete: PropTypes.func,
};
